import { TextFieldModule } from "@angular/cdk/text-field"
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  inject,
  output,
  signal,
} from "@angular/core"
import { FormsModule } from "@angular/forms"
import { MatButtonModule } from "@angular/material/button"
import { MatIconModule } from "@angular/material/icon"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { MatTooltipModule } from "@angular/material/tooltip"
import { ChatService, ChatSession } from "../chat.service"
import { MarkdownPipe } from "../markdown.pipe"

@Component({
  selector: "app-chat-panel",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    TextFieldModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MarkdownPipe,
  ],
  templateUrl: "./chat-panel.component.html",
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        width: 380px;
        height: 520px;
        border-radius: 16px;
        overflow: hidden;
        box-shadow: var(--mat-sys-level3);
        background: var(--mat-sys-surface-container);
        color: var(--mat-sys-on-surface);
      }
      .chat-header {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        background: var(--mat-sys-surface-container-high);
        border-bottom: 1px solid var(--mat-sys-outline-variant);
      }
      .chat-header h3 {
        margin: 0;
        flex: 1;
        font-size: 14px;
        font-weight: 600;
      }
      .messages {
        flex: 1;
        overflow-y: auto;
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .message {
        max-width: 85%;
        padding: 8px 12px;
        border-radius: 12px;
        font-size: 13px;
        line-height: 1.5;
      }
      .message.user {
        align-self: flex-end;
        background: var(--mat-sys-primary-container);
        color: var(--mat-sys-on-primary-container);
        border-bottom-right-radius: 4px;
        white-space: pre-wrap;
      }
      .message.assistant {
        align-self: flex-start;
        background: var(--mat-sys-surface-container-highest);
        color: var(--mat-sys-on-surface);
        border-bottom-left-radius: 4px;
      }
      .message.assistant ::ng-deep p {
        margin: 0 0 6px;
      }
      .message.assistant ::ng-deep p:last-child {
        margin-bottom: 0;
      }
      .message.assistant ::ng-deep ul,
      .message.assistant ::ng-deep ol {
        margin: 4px 0;
        padding-left: 20px;
      }
      .message.assistant ::ng-deep li {
        margin: 2px 0;
      }
      .message.assistant ::ng-deep code {
        background: var(--mat-sys-surface-variant);
        padding: 1px 4px;
        border-radius: 3px;
        font-size: 12px;
        font-family: monospace;
      }
      .message.assistant ::ng-deep pre {
        background: var(--mat-sys-surface-variant);
        padding: 8px;
        border-radius: 6px;
        overflow-x: auto;
        margin: 6px 0;
        font-size: 12px;
      }
      .message.assistant ::ng-deep pre code {
        background: none;
        padding: 0;
      }
      .message.assistant ::ng-deep h1,
      .message.assistant ::ng-deep h2,
      .message.assistant ::ng-deep h3,
      .message.assistant ::ng-deep h4 {
        margin: 8px 0 4px;
        font-size: inherit;
        font-weight: 600;
      }
      .message.assistant ::ng-deep a {
        color: var(--mat-sys-primary);
      }
      .message.assistant ::ng-deep table {
        border-collapse: collapse;
        width: 100%;
        margin: 6px 0;
        font-size: 12px;
      }
      .message.assistant ::ng-deep th,
      .message.assistant ::ng-deep td {
        border: 1px solid var(--mat-sys-outline-variant);
        padding: 4px 8px;
      }
      .message.assistant ::ng-deep th {
        background: var(--mat-sys-surface-variant);
        font-weight: 600;
      }
      .message.assistant ::ng-deep blockquote {
        border-left: 3px solid var(--mat-sys-primary);
        margin: 6px 0;
        padding: 4px 8px;
        opacity: 0.8;
      }
      .message.assistant ::ng-deep strong {
        font-weight: 600;
      }
      .message.assistant ::ng-deep strong,
      .message.assistant ::ng-deep b {
        font-weight: 600;
      }
      .typing-indicator {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        margin-left: 2px;
        vertical-align: middle;
      }
      .typing-indicator span {
        display: inline-block;
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: currentColor;
        opacity: 0.3;
        animation: pulse 1.2s ease-in-out infinite;
      }
      .typing-indicator span:nth-child(2) {
        animation-delay: 0.2s;
      }
      .typing-indicator span:nth-child(3) {
        animation-delay: 0.4s;
      }
      @keyframes pulse {
        0%,
        80%,
        100% {
          opacity: 0.3;
          transform: scale(0.8);
        }
        40% {
          opacity: 1;
          transform: scale(1);
        }
      }
      .empty-state {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: var(--mat-sys-on-surface-variant);
        font-size: 13px;
        padding: 24px;
        text-align: center;
      }
      .empty-state mat-icon {
        font-size: 40px;
        width: 40px;
        height: 40px;
        opacity: 0.5;
      }
      .error-bar {
        padding: 8px 12px;
        background: var(--mat-sys-error-container);
        color: var(--mat-sys-on-error-container);
        font-size: 12px;
      }
      .input-row {
        display: flex;
        align-items: flex-end;
        gap: 4px;
        padding: 8px 12px;
        border-top: 1px solid var(--mat-sys-outline-variant);
      }
      textarea {
        flex: 1;
        resize: none;
        border: 1px solid var(--mat-sys-outline);
        border-radius: 8px;
        padding: 8px 10px;
        font-size: 13px;
        font-family: inherit;
        background: var(--mat-sys-surface);
        color: var(--mat-sys-on-surface);
        outline: none;
        min-height: 36px;
        max-height: 200px;
        overflow-y: auto;
        box-sizing: border-box;
      }
      textarea:focus {
        border-color: var(--mat-sys-primary);
      }
      .session-list {
        flex: 1;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
      }
      .session-item {
        display: flex;
        align-items: center;
        border-bottom: 1px solid var(--mat-sys-outline-variant);
      }
      .session-item:hover {
        background: var(--mat-sys-surface-variant);
      }
      .session-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 10px 16px;
        cursor: pointer;
        min-width: 0;
      }
      .delete-btn {
        flex-shrink: 0;
        opacity: 0;
        transition: opacity 0.15s;
        color: var(--mat-sys-on-surface-variant);
        padding-right: 4px;
      }
      .session-item:hover .delete-btn {
        opacity: 1;
      }
      .session-title {
        font-size: 13px;
        font-weight: 500;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
      }
      .session-meta {
        font-size: 11px;
        color: var(--mat-sys-on-surface-variant);
        margin-top: 2px;
      }
    `,
  ],
})
export class ChatPanelComponent implements AfterViewChecked {
  protected readonly chatService = inject(ChatService)
  readonly close = output<void>()

  protected readonly inputText = signal("")
  protected readonly showHistory = signal(false)

  @ViewChild("messagesEnd") private messagesEnd!: ElementRef<HTMLElement>

  ngAfterViewChecked(): void {
    this.messagesEnd?.nativeElement.scrollIntoView({ behavior: "smooth" })
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.send()
    }
  }

  protected send(): void {
    const content = this.inputText().trim()
    if (!content || this.chatService.isStreaming()) return
    this.inputText.set("")
    void this.chatService.sendMessage(content)
  }

  protected clearConversation(): void {
    this.chatService.clearSession()
  }

  protected toggleHistory(): void {
    this.showHistory.set(true)
    void this.chatService.loadSessions()
  }

  protected openSession(id: string): void {
    void this.chatService.resumeSession(id)
    this.showHistory.set(false)
  }

  protected deleteSession(id: string): void {
    void this.chatService.deleteSession(id)
  }

  protected sessionLabel(session: ChatSession): string {
    if (session.name) return session.name
    return "Chat"
  }

  protected sessionMeta(session: ChatSession): string {
    const n = session.message_count
    const d = new Date(session.updated_at ?? session.created_at).toLocaleString([], {
      dateStyle: "short",
      timeStyle: "short",
    })
    return `${n} message${n !== 1 ? "s" : ""} \u00b7 ${d}`
  }
}
